/**
 * The clinical operations the legacy bridge cannot express as one request.
 *
 * `bridgeMap.js` is data: most legacy calls are one REST request and a shape.
 * The calls here are not — writing an order is the order plus the signature the
 * form captured; the Current Diagnosis / Current Procedures grid is two lists
 * main kept in one table; the ICD picker has to survive a rate limit it never
 * had. So each is a function, and `bridgeMap.js` names it with `local:`.
 *
 * Everything here answers in the exact legacy shape the call site parses —
 * main's code (OrderPopup, CurrentDiag, CurrentProc, DiagnosisCombobox, the
 * signature pad, the PDF save modal) is merged unchanged and reads these keys.
 */

import api, { ApiError } from './client';
import {
  id, dateOnly, instantAt, jsonRows, bool, okStatus, codedTerm, routeId, scheduleTimes,
} from './bridgeShapes';

// ============================================================================
// Codes and labels
// ============================================================================

/**
 * `ICD10:I10` -> `I10`. A code the vocabulary did not resolve is kept in
 * `uncoded_text` as `SYSTEM:code` (several joined by `'; '`); the grids print
 * the code, not the storage convention.
 */
export function stripCodeSystem(value) {
  const first = String(value ?? '').split(';')[0].trim();
  const match = /^(ICD10|ICD-10|CPT|HCPCS|SNOMED|RXNORM|LOINC|CVX):(.+)$/i.exec(first);

  return match ? match[2].trim() : first;
}

/**
 * Main's label for a diagnosis: `'CODE - description'` when both exist and
 * differ, otherwise whichever exists. The same rule as DiagnosisCombobox's
 * `formatDiagnosisLabel`, which parses these rows back apart — keyon stored the
 * label itself in `orders_diagnosis.diagnosis_code`, so every grid that reads
 * that key prints it, and the combobox splits it on `' - '`.
 */
export function diagnosisLabel(code, description) {
  const c = String(code ?? '').trim();
  const d = String(description ?? '').trim();

  if (c && d && d !== c && !d.startsWith(`${c} - `)) return `${c} - ${d}`;

  return d || c;
}

/** The row's own code, out of `codes[]`, else `uncoded_text`, else `code`. */
export function ownCode(row) {
  const codes = Array.isArray(row && row.codes) ? row.codes : [];
  const own = codes.find((entry) => !entry.role || entry.role === 'condition') || codes[0];

  if (own && own.code) return own.code;
  if (row && row.uncoded_text) return stripCodeSystem(row.uncoded_text);

  return (row && row.code) || '';
}

// ============================================================================
// Current Procedures
// ============================================================================

/**
 * A chart procedure (`ProcedureResource`) in the spellings Current Procedures
 * binds (CurrentProc.vue, OrdersPage.loadAllProcedures).
 *
 * Main's rows were `orders_diagnosis` `'Procedure'` rows: `procedure_code` held
 * the picked `'CODE - description'`, `diagnosis_code` the ICD it was done for,
 * and an ordered one carried its order's number and both signatures, which the
 * grid shows and offers to complete. A quick-add has no order and its row says
 * `0`, which is what the grid tests.
 */
export function chartProcedureRows(rows) {
  return (rows || []).map((row) => {
    const code = ownCode(row);
    const order = row.order || null;

    return {
      ...row,
      diagnosis_type: 'Procedure',
      procid: row.id,
      orddiagid: row.order_procedure_id ?? row.id,
      procedure_code: diagnosisLabel(code, row.description),
      procShorthand: row.short_name ?? '',
      diagnosis_code: row.indication ?? '',
      diagnose_code: row.indication ?? '',
      instruction: row.instructions ?? '',
      diagnosis_date: dateOnly(row.performed_from) ?? '',
      end_date: dateOnly(row.performed_to) ?? '',
      writer: (row.performing_provider && row.performing_provider.name) || row.performing_provider_name || '',
      order_id: order ? order.id : 0,
      nursesignature: (order && order.nurse_signature_name) || '',
      providersignature: (order && order.provider_signature_name) || '',
    };
  });
}

/**
 * `Diagnosis.PopulateDiagGrid` — main's single mixed list.
 *
 * keyon's `GetPDiagnosis` answered every `orders_diagnosis` row of the patient,
 * and each screen kept its own kind: Current Diagnosis the `'Diagnosis'` rows,
 * Current Procedures the `'Procedure'` rows. The port keeps the two kinds in
 * two resources, so both are read and returned together — which is also what
 * makes a procedure saved from Current Procedures appear on its next load
 * instead of vanishing into a list the grid never asked for.
 *
 * The procedures read is allowed to fail on its own (a caller who may read
 * diagnoses and not procedures still gets their Current Diagnosis).
 */
export async function populateDiagGrid(p, diagnosisRows) {
  const patientId = routeId(p.patientID ?? p.patientid ?? p.pid);

  if (patientId === null) return { gridhtml: true, data: [] };

  const [diagnoses, procedures] = await Promise.all([
    api.list(`/patients/${patientId}/diagnoses`, { per_page: 100 }),
    api.list(`/patients/${patientId}/procedures`, { per_page: 100 })
      .catch((error) => {
        if (error instanceof ApiError && (error.status === 403 || error.status === 404)) return { items: [] };
        throw error;
      }),
  ]);

  return {
    gridhtml: true,
    data: [...diagnosisRows(diagnoses.items), ...chartProcedureRows(procedures.items)],
  };
}

// ============================================================================
// ICD-10 lookup — keyon `icd_calls.php`
// ============================================================================

/** Search terms already answered this session, newest last; bounded. */
const icdAnswers = new Map();
const ICD_CACHE_SIZE = 50;

/**
 * `icd_calls.php` `{action: 'CodeLookUp' | 'Search', searchTerm}` ->
 * `GET /vocabularies/icd10?q=`, answered as `{result, actualResults}`.
 *
 * keyon's file sat outside the envelope dispatch, so nothing bridged it and
 * every ICD picker — Current Diagnosis, Current Procedures, the order popup,
 * the medication form, and main's new DiagnosisCombobox — called a backend
 * that no longer exists.
 *
 * The vocabulary routes allow 30 requests a minute per person, and the pickers
 * search on every keystroke (the combobox debounces at 250 ms). So a term
 * already answered is answered from memory, a term shorter than two characters
 * is not sent, and a refusal (rate limit, no `diagnoses:read`, upstream down)
 * comes back as "no results" with the reason — the pickers render an empty list
 * rather than a stack trace, which is what keyon did when NLM was unreachable.
 */
export async function icdLookup(p) {
  const term = String((p && (p.searchTerm ?? p.ICDCode)) ?? '').trim();

  if (term.length < 2) return { result: 0, actualResults: [] };

  const key = term.toLowerCase();

  if (icdAnswers.has(key)) {
    const cached = icdAnswers.get(key);
    icdAnswers.delete(key);
    icdAnswers.set(key, cached);

    return cached;
  }

  let terms;

  try {
    terms = await api.get('/vocabularies/icd10', { q: term, per_page: 25 });
  } catch (error) {
    if (error instanceof ApiError) {
      return { result: 0, actualResults: [], message: error.message };
    }

    throw error;
  }

  const actualResults = (Array.isArray(terms) ? terms : [])
    .filter((t) => t && t.code)
    .map((t) => ({ code: t.code, description: t.display || t.description || '' }));

  const answer = { result: actualResults.length, actualResults };

  icdAnswers.set(key, answer);
  if (icdAnswers.size > ICD_CACHE_SIZE) icdAnswers.delete(icdAnswers.keys().next().value);

  return answer;
}

/** For tests: forget every cached answer. */
export function forgetIcdAnswers() {
  icdAnswers.clear();
}

// ============================================================================
// The signing pre-check — `MedicationAdmin.VerifyAccountSignature`
// ============================================================================

/**
 * `GET /me/order-signature` in the shape main's signature pad reads
 * (MedadminshortOrderTemplate.verifyAccountSignature): code `'1'` with the
 * spaced name and the legacy account type (`'Registered Nurse'` /
 * `'Provider'`, which the pad compares against), or code `'0'` with main's
 * specific sentence, which the pad alerts instead of opening.
 *
 * The question is always about the signed-in person. The legacy call named the
 * account to check in its body (`provsubaccnt`); nothing in the payload is read.
 */
export function signerShape(signer) {
  const label = (signer && signer.account_type_label) || '';

  if (signer && signer.may_sign) {
    return okStatus('200 Successfull', {
      code: '1',
      providerName: signer.display_name,
      accountType: label,
      message: 'Account Verified',
    });
  }

  return okStatus('200 Successfull', {
    code: '0',
    providerName: 'NA',
    accountType: label,
    message: (signer && signer.refusal && signer.refusal.message)
      || 'Account could not be verified for signing.',
  });
}

// ============================================================================
// Orders — `InsertNewOrder`, `GetOrderDetails`
// ============================================================================

const ORDER_TYPES = {
  'physicians order': 'physician',
  "physician's order": 'physician',
  physician: 'physician',
  'nurses order': 'nurse',
  "nurse's order": 'nurse',
  nurse: 'nurse',
  'verbal order': 'verbal',
  verbal: 'verbal',
};

/** `'Physicians Order'` -> `'physician'`, the `OrderType` enum. */
export function orderTypeSlug(label) {
  return ORDER_TYPES[String(label ?? '').trim().toLowerCase()] ?? String(label ?? '').trim().toLowerCase();
}

// The order form's two tri-state selects: ABN `0/1/2` (N/A, No, Yes) and read
// back `NA/No/Yes`. The column stores `not_applicable/no/yes`.
const TRISTATE = {
  0: 'not_applicable', na: 'not_applicable', 'n/a': 'not_applicable', '': 'not_applicable',
  1: 'no', no: 'no',
  2: 'yes', yes: 'yes',
};

function tristate(value) {
  return TRISTATE[String(value ?? '').trim().toLowerCase()] ?? undefined;
}

/** A value only when it is present, so an empty form field does not become a 422. */
function present(value) {
  const text = value === null || value === undefined ? '' : String(value).trim();

  return text === '' ? undefined : text;
}

/** An integer when the form field holds one, else nothing (the column is an integer). */
function wholeNumber(value) {
  const text = String(value ?? '').trim();

  return /^\d+$/.test(text) ? Number(text) : undefined;
}

/** A date the API can read, or nothing. `'2026-09-10'` passes; a locale string does not. */
function isoDay(value) {
  const text = String(value ?? '').trim();

  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : undefined;
}

/**
 * The diagnosis a line was ordered for, in the pair the API reads. The code is
 * the column's (32 characters); a term typed into the code box that is not a
 * code travels as the description, where the promotion keeps it as a name.
 */
function lineDiagnosis(code, description) {
  const c = present(code);
  const d = present(description);

  if (c && c.length > 32) return { diagnosis_code: undefined, diagnosis_description: d ?? c };

  return { diagnosis_code: c, diagnosis_description: d };
}

/** One of `newMeds`, as `StoreOrderRequest` names it. */
export function orderMedicationRow(m) {
  return {
    name: present(m.medName ?? m.medname ?? m.name),
    short_name: present(m.medShorthand),
    total_quantity: wholeNumber(m.quantity),
    dose_amount: present(m.dosage) && !Number.isNaN(Number(m.dosage)) ? Number(m.dosage) : undefined,
    dose_unit: present(m.unitType ?? m.dosageForm),
    frequency_code: present(m.frequency),
    duration: present(m.duration),
    route: present(m.route),
    alternate_route: present(m.altRoute),
    is_prn: bool(m.prn),
    instructions: present(m.instructions),
    start_on: isoDay(m.startDate),
    end_on: isoDay(m.endDate),
    is_prepour_eligible: bool(m.prepour),
    is_nebulizer: bool(m.nebulizer),
    requires_administration: bool(m.administration),
    ndc_code: present(m.ndcnumber),
    rxnorm_code: present(m.rxnorns),
    ...lineDiagnosis(m.diagCode, m.diagDescription),
  };
}

/** One of `newProcs`. The picker writes `'99213 - Office visit'` into the code. */
export function orderProcedureRow(proc) {
  const picked = codedTerm(proc.code);

  return {
    code: picked.code ?? present(proc.shorthand),
    short_name: present(proc.shorthand),
    description: picked.text && picked.text !== picked.code ? picked.text : undefined,
    instructions: present(proc.instructions),
    start_on: isoDay(proc.startDate),
    end_on: isoDay(proc.endDate),
    ...lineDiagnosis(proc.diagCode, proc.diagDescription),
  };
}

/** What every order written from one OrderPopup save shares: the header. */
function orderHeader(p) {
  const npi = String(p.npi ?? '').replace(/\D/g, '');

  return {
    patient_id: id(p.pid),
    order_type: orderTypeSlug(p.orderType),
    ordered_at: instantAt(p.orderDate, p.orderTime) ?? undefined,
    description: present(p.description),
    abn_delivered: tristate(p.abnDelivery),
    order_read_back: tristate(p.readOrderBack),
    prescriber_npi: npi.length === 10 ? npi : undefined,
    prescriber_taxonomy_code: present(p.primaryTaxonomy),
  };
}

/**
 * The `InsertNewOrder` payload OrderPopup builds, as the `POST /orders` bodies
 * main writes: **one order per ordered medication and one per ordered
 * procedure** (keyon `InsertNewOrder`: "Create a separate order for each
 * medication", and for each procedure). The grids depend on it: the Orders page
 * shows one row per order with that order's medication (`med_*`), and each
 * line is signed, held and discontinued as its own order — one order holding
 * three medications would list one of them and sign all three at once.
 *
 * Each order is atomic on its own (the line, and what it puts on the chart).
 *
 * The staged diagnoses (`newDiags`) are `chart_diagnoses` and travel with the
 * first order: main writes them to Current Diagnosis at order number 0 — the
 * chart, not an order — once per save, and the API does the same with them.
 *
 * The prescriber is named by the NPI the form looked up; the server resolves it
 * against the registry and copies nothing the form typed.
 *
 * @returns {Array<{kind: string, index: number, label: string, source: object, body: object}>}
 */
export function newOrderBodies(p) {
  const header = orderHeader(p);
  const staged = jsonRows(p.newDiags)
    .map((d) => ({
      code: present(d.code),
      description: present(d.description),
      onset_on: isoDay(d.startDate),
    }))
    .filter((d) => d.code || d.description);

  const lines = [
    ...jsonRows(p.newMeds).map((m, index) => ({
      kind: 'medication',
      index,
      source: m,
      label: present(m.medName ?? m.medname ?? m.name) || 'a medication',
      body: { ...header, order_category: 'medication', medications: [orderMedicationRow(m)] },
    })),
    ...jsonRows(p.newProcs).map((proc, index) => ({
      kind: 'procedure',
      index,
      source: proc,
      label: present(proc.code) || present(proc.shorthand) || 'a procedure',
      body: { ...header, order_category: 'procedure', procedures: [orderProcedureRow(proc)] },
    })),
  ];

  if (lines.length > 0 && staged.length > 0) lines[0].body.chart_diagnoses = staged;

  return lines;
}

/** The single-body form, for a caller that wants the first order only. */
export function newOrderBody(p) {
  const [first] = newOrderBodies(p);

  return first ? first.body : { ...orderHeader(p), medications: [], procedures: [] };
}

/**
 * The administration times a medication was ordered with, as the `'HH:MM'`
 * slots the schedule endpoint takes: `[]` for none (PRN, "As needed"), or
 * `undefined` when times were given and none could be read.
 */
export function lineAdministrationTimes(m) {
  const slots = Array.isArray(m.slotedtimes) && m.slotedtimes.length > 0 ? m.slotedtimes : null;
  const listed = present(m.administrationTimes);

  if (!slots && (!listed || /^as needed$/i.test(listed))) return [];

  return scheduleTimes(slots || listed);
}

/** Main administered a line when its Administration box was ticked. */
function isAdministered(m) {
  return bool(m.administration) || m.administration === 'Administered';
}

/**
 * The pharmacy block OrderPopup requires on every new medication, as
 * `POST /patients/{patient}/pharmacies` takes it — or nothing when main would
 * have skipped it (keyon needs the name, the NPI and the address).
 */
function linePharmacy(m) {
  const name = present(m.pharmacyName);
  const npi = present(m.pharmacyNpi);
  const address = present(m.pharmacyAddress);

  if (!name || !npi || !address) return null;

  return {
    name,
    npi,
    address,
    dea: present(m.pharmacyDea),
    email: present(m.pharmacyEmail),
    phone: present(m.pharmacyPhone),
  };
}

/** An API refusal's sentence; anything else is not ours to swallow. */
function refusalOf(error) {
  if (!(error instanceof ApiError)) throw error;

  return error.message;
}

/**
 * `InsertNewOrder`: write the orders, then what main wrote with them.
 *
 * After the orders (see `newOrderBodies`), three follow-ups, each its own
 * endpoint in the port, in this order:
 *
 *  - **The administration times** of a medication ordered with Administration
 *    ticked. Main wrote them as the line's `medicationlog` slots, which is what
 *    the MAR runs on; here they are the line's schedule
 *    (`PUT /medications/{id}/administration-schedule`). Without this every
 *    medication ordered here reached the MAR with no schedule. They go
 *    **before the signature**: main writes them in the insert, so the
 *    signature is to the order with its times, and the API refuses times on a
 *    line whose order a signature is already on (`order_already_signed`).
 *    Sent after it, they were refused on every signed save, and the
 *    medication reached the MAR with nothing due (probe p25).
 *  - **The signature the form captured.** Main stores the nurse's signature on
 *    a nurse or verbal order, and the provider's on a physician order, as part
 *    of the insert. Here a signature is an append-only write
 *    (`POST /orders/{order}/signatures`) checked against the signer's own
 *    account type and credential — so it is sent only when the person at the
 *    keyboard may fill that half (the pre-check says so). A name typed for
 *    somebody else is never posted as their signature: the orders are saved,
 *    left waiting for it, and the message says why. Nor is it posted on an
 *    order whose times were not recorded: signed without them, the order
 *    could not be given them until it was co-signed.
 *  - **The pharmacy** each medication names, onto the chart's pharmacy list
 *    (`POST /patients/{patient}/pharmacies`), which is where main's
 *    `patpharmacy` row made it appear. The port has no per-line pharmacy link
 *    on an ordered medication; the chart's list is its nearest counterpart.
 *
 * A follow-up that is refused does not undo the orders: the message says what
 * was not recorded, as it does for the signature.
 *
 * Answers `{code: '1', …}` only when every order was written, so OrderPopup's
 * success branch clears the form only then. A refused first order is an
 * `ApiError` the bridge re-emits as `{code: 0, error}`. A refusal part-way
 * answers `{code: '0', error, saved_lines}`: the orders already written stay
 * written (as each line is its own order on main too), and `saved_lines` names
 * them so the form can drop them rather than order them twice on a retry.
 */
export async function insertNewOrder(p) {
  const lines = newOrderBodies(p);
  const saved = [];
  let refusal = null;

  for (const line of lines) {
    try {
      saved.push({ ...line, order: await api.post('/orders', line.body) });
    } catch (error) {
      if (!(error instanceof ApiError) || saved.length === 0) throw error;

      refusal = { line, message: error.message };
      break;
    }
  }

  const notes = [];
  const pharmacies = new Map();
  // The orders whose administration times were not recorded, by the line's
  // label: left unsigned below.
  const unscheduled = new Map();

  for (const { kind, label, source, order } of saved) {
    if (kind !== 'medication') continue;

    const medication = Array.isArray(order && order.medications) ? order.medications[0] : null;
    const times = lineAdministrationTimes(source);

    if (isAdministered(source)) {
      if (times === undefined) {
        notes.push(`the administration times for ${label} could not be read and were not recorded.`);
        unscheduled.set(order, label);
      } else if (medication && medication.id) {
        try {
          await api.put(`/medications/${medication.id}/administration-schedule`, { times });
        } catch (error) {
          notes.push(`the administration times for ${label} were not recorded: ${refusalOf(error)}`);
          unscheduled.set(order, label);
        }
      }
    }

    const pharmacy = linePharmacy(source);

    // One pharmacy named on several lines is assigned once, with the first
    // line's details (the form copies the same block onto each).
    const key = pharmacy && `${pharmacy.name.toLowerCase()}|${pharmacy.npi}`;

    if (pharmacy && !pharmacies.has(key)) pharmacies.set(key, pharmacy);
  }

  const orderType = lines.length > 0 ? lines[0].body.order_type : orderTypeSlug(p.orderType);
  const slot = orderType === 'physician' ? 'provider' : 'nurse';
  const typed = present(slot === 'nurse' ? p.nurseSignature : (p.provSignature ?? p.providerSignature));
  let signed = 0;

  if (typed) {
    let signatureNote = null;

    try {
      const signer = await api.get('/me/order-signature');

      if (signer && signer.may_sign && signer.role === slot) {
        for (const { order } of saved) {
          if (unscheduled.has(order)) {
            notes.push(`your signature was not recorded on the order for ${unscheduled.get(order)}: an order is `
              + 'signed with its administration times, so sign it once they are recorded.');
            continue;
          }

          try {
            await api.post(`/orders/${order.id}/signatures`, {
              signature: typed,
              signed_on: isoDay(slot === 'nurse' ? p.nurseSigDate : p.provSigDate),
            });
            signed += 1;
          } catch (error) {
            signatureNote = signatureNote || refusalOf(error);
          }
        }
      } else {
        signatureNote = (signer && signer.refusal && signer.refusal.message)
          || (slot === 'nurse'
            ? 'Only a Registered Nurse can sign the Nurse Signature field.'
            : 'Only a Provider can sign the Provider Signature field.');
      }
    } catch (error) {
      signatureNote = refusalOf(error);
    }

    if (signatureNote) {
      notes.push(`your signature was not recorded: ${signatureNote} The order is waiting for that signature.`);
    }
  }

  for (const pharmacy of pharmacies.values()) {
    try {
      await api.post(`/patients/${id(p.pid)}/pharmacies`, pharmacy);
    } catch (error) {
      notes.push(`${pharmacy.name} was not added to the patient's pharmacies: ${refusalOf(error)}`);
    }
  }

  const first = saved[0] && saved[0].order;
  const savedLines = {
    medications: saved.filter((line) => line.kind === 'medication').map((line) => line.index),
    procedures: saved.filter((line) => line.kind === 'procedure').map((line) => line.index),
  };
  const noun = saved.length > 1 ? 'The orders were saved' : 'The order was saved';
  const followUps = notes.length > 0 ? ` ${noun}, but ${notes.join(' Also, ')}` : '';

  if (refusal) {
    const unsaved = lines.slice(saved.length).map((line) => line.label).join(', ');

    return {
      status: 'Failed',
      code: '0',
      saved_lines: savedLines,
      error: `Only part of this order was saved. Ordered: ${saved.map((line) => line.label).join(', ')}. `
        + `Not ordered: ${unsaved} — ${refusal.message} The ordered lines have been taken off the form; `
        + `correct the rest and save again.${followUps}`,
    };
  }

  return {
    status: 'Successful',
    code: '1',
    id: first && first.id,
    ordernumber: first && String(first.id),
    order_display_number: first && first.order_number,
    orders: saved.map(({ order }) => order && order.id),
    saved_lines: savedLines,
    signature_recorded: typed ? signed === saved.length && saved.length > 0 : false,
    message: notes.length > 0 ? `${noun}, but ${notes.join(' Also, ')}` : 'Order has been successfully submitted.',
  };
}

/**
 * `GetOrderDetails` — keyon answered ONE line in its legacy spellings: the
 * medication (`type` medication) or the procedure (`type` procedure) of the
 * order, with the order's two signatures on the procedure row.
 */
export function orderDetailRow(order, p) {
  const o = order || {};
  const wantProcedure = String((p && p.type) || '').toLowerCase() === 'procedure';

  if (wantProcedure) {
    const procs = Array.isArray(o.procedures) ? o.procedures : [];
    const proc = procs.find((row) => String(row.id) === String(p.id)) || procs[0];

    if (!proc) return {};

    const sig = o.signatures_summary || {};

    return {
      ...proc,
      code: diagnosisLabel(proc.code, proc.description),
      procedure_code: diagnosisLabel(proc.code, proc.description),
      procShorthand: proc.short_name ?? '',
      diagnosis_code: proc.diagnosis_code ?? '',
      instruction: proc.instructions ?? '',
      instructions: proc.instructions ?? '',
      startDate: proc.start_on ?? '',
      diagnosis_date: proc.start_on ?? '',
      endDate: proc.end_on ?? '',
      end_date: proc.end_on ?? '',
      writer: proc.created_by_name ?? '',
      procId: proc.id,
      diagnosis_id: proc.id,
      diagid: proc.id,
      nursesignature: (sig.nurse && sig.nurse.signature_name) || '',
      providersignature: (sig.provider && sig.provider.signature_name) || '',
    };
  }

  const meds = Array.isArray(o.medications) ? o.medications : [];
  const med = meds.find((row) => String(row.id) === String(p && p.id)) || meds[0];

  if (!med) return {};

  return {
    ...med,
    medentryid: med.id,
    medName: med.name,
    medname: med.name,
    dosage: med.dose_amount,
    frequency: med.frequency_code,
    route: med.route,
    altRoute: med.alternate_route,
    diagDescription: med.diagnosis_code,
    startDate: med.start_on,
    endDate: med.end_on,
    unitType: med.dose_unit,
    instructions: med.instructions,
    prn: med.is_prn ? '1' : '0',
    ndcnumber: med.ndc_code,
    rxnorns: med.rxnorm_code,
    quantity: med.total_quantity,
    available: med.available_quantity,
    medchangetype: med.change_type,
    prepour: med.is_prepour_eligible ? 1 : 0,
    nebulizer: med.is_nebulizer ? 1 : 0,
    administration: med.requires_administration ? 1 : 0,
  };
}

// ============================================================================
// Reports — the PDF extraction save modal
// ============================================================================

/**
 * A stored file's route key: the 32-hex uid when the caller has one, else the
 * numeric id the pacmny-be screens were built on (the C-CDA and PDF routes
 * resolve both; the uid is preferred because a sequential id walks a tenant's
 * files).
 */
export function fileRef(value) {
  const text = String(value ?? '').trim();

  if (/^[a-f0-9]{32}$/i.test(text)) return text.toLowerCase();

  return routeId(text);
}

/**
 * `#saveExtraction` — commit a PDF extraction into a report.
 *
 * Main's save modal sends `{id, date, npi, extractions, procedure}` and no
 * patient (pacmny-be read the chart from the session), and since #237 no
 * document type: a procedure is required instead. The chart is the SPA's open
 * one. The API's "needs a category or a procedure" refusal comes back as the
 * message the modal branches on.
 */
export async function saveExtraction(p, patientId) {
  try {
    const report = await api.post('/reports', {
      pdf_document_id: fileRef(p.id ?? p.pdfId),
      patient_id: patientId,
      report_date: dateOnly(p.date ?? p.reportDate),
      npi: present(p.npi),
      category_code: present(p.category ?? p.categoryCode),
      procedure_code: present(p.procedure ?? p.procedureCode),
      detail: present(p.detail),
      extractions: p.extractions || p.pages,
    });

    return { message: 'extractionSavedMessage', id: report && report.id };
  } catch (error) {
    if (error instanceof ApiError && error.code === 'missing_category_or_procedure') {
      return { message: 'missingCategoryOrProcedure', error: error.message };
    }

    throw error;
  }
}

/**
 * A vocabulary page as the save modal's pickers list it: `{ccd, desc}`, where
 * `ccd` is what is sent back as the report's code. Main keyed these by
 * pacmny-be's concept ids; the port's report takes the code itself.
 */
export function vocabularyChoices(items) {
  return (items || []).map((term) => ({
    ...term,
    ccd: term.code,
    desc: term.display || term.description || term.code,
  }));
}

/**
 * The open chart's saved reports, in `getAllData`'s legacy spellings — what the
 * Reports page (`store/modules/report.js`) and the save modal's "recent
 * choices" read (`allData.reports`, `allData.recent.report.ccd`,
 * `allData.ccds`).
 *
 * pacmny-be built these null-safely since main's fix: a report may have a
 * category, a procedure, or both, and one missing either is still listed.
 * Newest first, like the legacy `recent` CTE.
 */
export function reportsLegacyShape(reports) {
  const rows = [];
  const ccds = {};
  const recentProcedures = [];

  (reports || []).forEach((report) => {
    const procedures = Array.isArray(report.procedure_codes) ? report.procedure_codes : [];
    const category = report.category || null;
    const procedure = procedures[0] || null;

    [category, ...procedures].forEach((concept) => {
      if (concept && concept.code && !ccds[concept.code]) {
        ccds[concept.code] = { code: concept.code, desc: concept.display || concept.code };
      }
    });

    procedures.forEach((concept) => {
      if (concept && concept.code && !recentProcedures.includes(concept.code)) recentProcedures.push(concept.code);
    });

    rows.push({
      id: report.id,
      when: report.report_date,
      npi: (report.ordering_provider && report.ordering_provider.npi) || null,
      category: category ? category.code : null,
      procCcd: procedure ? procedure.code : null,
      ccdDescription: procedure ? procedure.display : (category ? category.display : ''),
    });
  });

  return {
    reports: rows,
    recent: { report: { ccd: recentProcedures } },
    ccds,
  };
}

// ============================================================================
// Chart PDFs and C-CDAs — the workbench lists `getAllData` carried
// ============================================================================

/**
 * `YYYY-MM-DD HH:MM:SS` in the viewer's own time zone: main's `recordedAt`
 * (pacmny-be `Utility::withinSeconds` for a PDF, `date('Y-m-d H:i:s')` for a
 * C-CDA). The API's instants are UTC, and the date part of a UTC string is the
 * wrong calendar day for an evening upload anywhere west of Greenwich.
 */
function localTimestamp(instant) {
  const when = new Date(instant);

  if (!instant || Number.isNaN(when.getTime())) return instant ? String(instant) : '';

  const pad = (n) => String(n).padStart(2, '0');

  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())} `
    + `${pad(when.getHours())}:${pad(when.getMinutes())}:${pad(when.getSeconds())}`;
}

/**
 * Upload order, ties broken by the sequential id, which only ever grows.
 * `uploaded_at` has whole seconds, and two uploads in one second otherwise come
 * back in whatever order the database likes - and the upload screen opens the
 * list's newest end straight after an upload.
 */
function byUpload(a, b) {
  const at = (Date.parse(a.uploaded_at) || 0) - (Date.parse(b.uploaded_at) || 0);

  return at !== 0 ? at : Number(a.id) - Number(b.id);
}

/**
 * The open chart's uploaded PDFs as `getAllData.pdfs` — the Clinical PDFs
 * dropdown (`store/modules/pdf.js`) and `PdfContentBody`.
 *
 * - **A row's `id` is the document's 32-hex uid**, and that is what the viewer,
 *   the grabs, the extraction preview, the report draft and the report save
 *   address it by (`fileRef` in each bridge entry). A sequential id in those
 *   URLs is a key that walks the organization's other documents.
 * - **Uploads only.** Main listed LJ_pdfs, the PDFs somebody uploaded; the
 *   rendered extract a saved report produces (`report_extract`) belongs to the
 *   report, and listing it here offers it up for extraction a second time.
 * - **Newest first**, as main's `recordedAt DESC`: FileUpload.vue opens index 0
 *   after an upload.
 * - `displayName` is main's: the upload's timestamp.
 */
export function pdfsLegacyShape(documents) {
  const rows = (documents || [])
    .filter((doc) => doc && doc.category !== 'report_extract')
    .sort((a, b) => byUpload(b, a))
    .map((doc) => {
      const recordedAt = localTimestamp(doc.uploaded_at);

      return {
        id: doc.public_id,
        publicId: doc.public_id,
        recordedAt,
        displayName: recordedAt,
        filename: doc.filename || '',
        pageCount: doc.page_count ?? null,
        isInChart: Boolean(doc.is_in_chart),
      };
    });

  return { pdfs: rows };
}

/**
 * The open chart's C-CDAs as `getAllData.cdas` — the CDA dropdown
 * (`store/modules/cda.js`), the page and its side bar.
 *
 * - `id` is the uid, as for PDFs; `#requestAutoAddCda`, `#downloadCda`,
 *   `#deleteCda` and `/file/cdas#requestCda` address it by that.
 * - **Oldest first**, as main's LJ_uccda order: FileUpload.vue opens the
 *   *last* C-CDA after an upload.
 * - `name` is main's stream name, the uploaded file's; the dropdown shows it.
 * - `isSaved` is main's "this C-CDA is in the chart": committed. `status` is
 *   the import's state (`uploaded`, `scanning`, `staged`, `committed`).
 * - **The extraction maps start empty.** Main built `byDateAndKind` and the
 *   `e<kind>` collections from pacmny-be's staging tables in the same blob;
 *   the port reads a C-CDA's staged items only for the one open on the CDA
 *   page (`GET /cda-documents/{id}/staged-items`, stores/chartCdaStagedItems.js)
 *   and lays them over its row with `withStagedItems` below. The maps are
 *   present while empty because the side bar's getters index into them
 *   unguarded.
 * - **`extractionShown` is false until then, and Save CDA is off while it is.**
 *   Saving commits the scan's staged medications, problems, allergies, results
 *   and vaccinations into the chart; in main the clinician saw them in the side
 *   bar first, and committing what nobody was shown is not main's Save
 *   (CdaContentBody.vue, `login/cda/getCdaSaveUnavailableReason`). Once they are
 *   shown, nothing else holds Save back: what the document left undated or
 *   uncoded is a note on the item (`reviewNotes`), not a blocker.
 */
export function cdasLegacyShape(documents) {
  const rows = (documents || [])
    .filter(Boolean)
    .sort(byUpload)
    .map((doc) => ({
      id: doc.public_id,
      publicId: doc.public_id,
      recordedAt: localTimestamp(doc.uploaded_at),
      name: doc.filename || doc.title || '',
      isSaved: Boolean(doc.is_in_chart),
      status: doc.status || null,
      ...emptyCdaExtraction(),
      headerProviders: [],
      extractionShown: false,
      reviewNotes: [],
      notStaged: [],
    }));

  return { cdas: rows };
}

function emptyCdaExtraction() {
  return {
    lengthOfByDateAndKind: 0,
    byDateAndKind: [],
    byKindAndDate: {},
    emeds: {},
    eprocedures: {},
    ediagnosiss: {},
    eallergies: {},
    emeasures: {},
    eprocmeas: {},
    evaccines: {},
  };
}

/** A staged date as main's side bar keys it (`lo::date`): `YYYY-MM-DD`, or null. */
function stagedDate(value) {
  const match = value ? String(value).match(/^(\d{4}-\d{2}-\d{2})/) : null;

  return match ? match[1] : null;
}

/** `128.000000` -> `128`; a reading is shown as the document stated it. */
function stagedValue(row) {
  const numeric = row.value_numeric;

  if (numeric !== null && numeric !== undefined && numeric !== '' && !Number.isNaN(Number(numeric))) {
    return String(Number(numeric));
  }

  return row.value_text ?? '';
}

/** Main's ordering: newest `lo` first, then newest `hi`, procedures first within a day. */
function byStagedDate(a, b) {
  const lo = (b.lo || '1900-01-01').localeCompare(a.lo || '1900-01-01');
  if (lo !== 0) return lo;
  const hi = (b.hi || '1900-01-01').localeCompare(a.hi || '1900-01-01');
  if (hi !== 0) return hi;
  const rank = (kind) => (kind === 'eprocedure' ? 'A' : kind);
  return rank(a.kind).localeCompare(rank(b.kind));
}

/**
 * `GET /cda-documents/{id}/staged-items` (`StagedItemsResource`) as the side bar
 * main's pacmny-be built from its `A_e<x>` staging tables: `byDateAndKind`,
 * `byKindAndDate` and one `e<kind>` collection per kind, keyed by row id.
 *
 * - **Kinds are main's.** `diagnoses` rows are main's `ediagnosis`, or
 *   `eallergy` when their `category` says allergy (both stage into
 *   `conditions`); `measurements` are `emeasure`, and say whether they are a
 *   vital sign (`isVitalSign`, the concept's flag), which is how the side bar
 *   files them under Vitals or Labs; `medications` are `emedication`,
 *   `vaccinations` `evax`, `procedures` `eprocedure`.
 * - **The label is the row's own** (`desc`, a result's `dname`): main looked it
 *   up through `ccds`, the code catalogue pacmny-be shipped, which the port does
 *   not. `ccd`/`codes` are a key per row, never a catalogue id, present only
 *   because the getters skip a row without one.
 * - `htmlname` is the narrative anchor, which the side bar scrolls the document
 *   to when a row is clicked.
 * - No result is nested under a panel (`eprocmeas` is empty): the port keeps a
 *   result's panel on the result, not as a staged procedure.
 */
export function cdaStagedSideBar(staged) {
  const extraction = emptyCdaExtraction();
  const entries = [];
  const key = (kind, row) => `staged-${kind}-${row.id}`;
  // The review's notes on each item, by the set's own kind name and id:
  // `missing_date`, `uncoded`. Ids are per table, so the kind is part of the key.
  const notes = reviewNotesOf(staged);
  const flagsOf = (wire, row) => notes
    .filter((note) => note.kind === wire && note.id === row.id)
    .map((note) => note.reason);
  const add = (collection, kind, wire, row, fields) => {
    const lo = stagedDate(fields.loAt);
    const hi = stagedDate(fields.hiAt);
    const ccd = fields.ccd || key(kind, row);
    const label = String(row.display_name || row.name || row.uncoded_text || '');
    const { loAt, hiAt, ...rest } = fields;

    extraction[collection][row.id] = {
      id: row.id,
      kind,
      // The set's own name for the kind, which the review's writes address the item by.
      wire,
      lo,
      hi,
      lodate: lo,
      lotimestamp: loAt || null,
      hitimestamp: hiAt || null,
      htmlname: row.narrative_anchor || null,
      desc: label,
      ccd,
      codes: [ccd],
      flags: flagsOf(wire, row),
      ...rest,
    };
    entries.push({ kind, id: row.id, lo, hi });
  };

  const rows = (name) => (staged && Array.isArray(staged[name]) ? staged[name] : []);

  rows('measurements').forEach((row) => add('emeasures', 'emeasure', 'measurements', row, {
    loAt: row.effective_start,
    hiAt: row.effective_end,
    ccd: row.concept_code || null,
    dname: String(row.display_name || ''),
    value: stagedValue(row),
    units: row.unit || '',
    isVitalSign: row.is_vital_sign === true,
    panel: row.panel_display || null,
    weight: 0,
  }));

  rows('diagnoses').forEach((row) => {
    const isAllergy = row.category === 'allergy';

    add(isAllergy ? 'eallergies' : 'ediagnosiss', isAllergy ? 'eallergy' : 'ediagnosis', 'diagnoses', row, {
      loAt: row.onset_at,
      hiAt: row.abated_at,
      isallergy: isAllergy,
      // Whether the document gave the allergen a code (main's hasAllergenCode).
      // Without one the side bar shows the substance - the narrative's name for
      // what the patient is allergic to - and not a code that is not the
      // substance's. A problem has no allergen, so for it this is not asked.
      hasallergencode: isAllergy ? row.has_allergen_code === true : true,
      substance: row.substance || null,
      status: row.clinical_status || null,
    });
  });

  rows('medications').forEach((row) => add('emeds', 'emedication', 'medications', row, {
    loAt: row.effective_start,
    hiAt: row.effective_end,
    dname: String(row.display_name || row.name || ''),
    name: row.name || null,
    instructions: row.instructions || null,
  }));

  rows('vaccinations').forEach((row) => add('evaccines', 'evax', 'vaccinations', row, {
    loAt: row.administered_at,
    hiAt: null,
  }));

  rows('procedures').forEach((row) => add('eprocedures', 'eprocedure', 'procedures', row, {
    loAt: row.effective_start,
    hiAt: row.effective_end,
  }));

  entries.sort(byStagedDate);

  extraction.byDateAndKind = entries;
  extraction.lengthOfByDateAndKind = entries.length;
  entries.forEach((entry) => {
    if (!extraction.byKindAndDate[entry.kind]) extraction.byKindAndDate[entry.kind] = [];
    extraction.byKindAndDate[entry.kind].push(entry);
  });

  return extraction;
}

/**
 * The review's notes of a staged set (`review_notes`): the items the document
 * left undated (`missing_date`, with the `field` a correction sets) or uncoded
 * (`uncoded`). They flag; they do not block - Save commits every staged item, as
 * main's does (D6 default 2026-10-21) - and name their item by the set's own
 * kind and id, for the review to show beside it and to act on.
 */
export function reviewNotesOf(staged) {
  const allergies = new Set((staged && Array.isArray(staged.diagnoses) ? staged.diagnoses : [])
    .filter((row) => row.category === 'allergy')
    .map((row) => row.id));

  return (staged && Array.isArray(staged.review_notes) ? staged.review_notes : [])
    .map((note) => ({
      kind: note.kind,
      id: note.item_id,
      field: note.field || null,
      reason: note.reason,
      label: note.label || '',
      // `diagnoses` holds problems and allergies; the review says which.
      isAllergy: note.kind === 'diagnoses' && allergies.has(note.item_id),
    }));
}

/**
 * A C-CDA row with its staged items laid over it, as the side bar reads them,
 * and `extractionShown` set: the clinician can now see what Save would commit.
 * `reviewNotes` are the API's (`review_notes`).
 */
export function withStagedItems(row, staged) {
  return {
    ...row,
    ...cdaStagedSideBar(staged),
    extractionShown: true,
    reviewNotes: reviewNotesOf(staged),
    notStaged: notStagedOf(staged),
  };
}

/**
 * What the scan could not stage at all (`not_staged`): an entry that names itself
 * nowhere (`unnamed`), or a result whose codes the vocabulary does not know
 * (`uncoded_result`), a statement the document says is NOT so (`negated`: "no
 * known allergies" is not an allergy), a procedure or an encounter the document
 * only plans, requests or books (`planned`: main stages only what happened), one
 * that never says it happened (`not_performed`: no mood, or one main does not know), or an
 * entry holding nothing its section can read (`unreadable`, with the `element` it
 * did hold). Not in any collection -
 * there is no row - so the review lists them apart, for a reviewer who would
 * otherwise have to wonder what the document held that the import did not. The API
 * keeps the first few hundred; when there were more, its last line is
 * `{reason: 'more', count}` for the rest, which the review shows as such rather
 * than as an entry.
 *
 * Two kinds of line are a count and not an entry: `more`, and `over_limit` -
 * what the import's bounds left unread, `count` entries of the `section` or (with
 * `unit` `sections`) sections of the document. A document past a bound is not
 * read to the end, and the review says how much of it was not.
 */
export function notStagedOf(staged) {
  return (staged && Array.isArray(staged.not_staged) ? staged.not_staged : [])
    .map((entry) => ({
      kind: entry.kind || '',
      reason: entry.reason || '',
      section: entry.section || '',
      label: entry.label || '',
      code: entry.code || '',
      count: Number(entry.count) > 0 ? Number(entry.count) : 0,
      unit: entry.unit || '',
      element: entry.element || '',
    }));
}

/**
 * What the side bar's "Move selected medications to Current" sends main's
 * `cdaMedicationImport` - `{key, htmlname, lo, hi, name}` per medication - as
 * the API's `items` (`{key, html_name, date, end_date}`).
 *
 * **One item per row, with what tells the rows apart.** A drug taken in two
 * courses has two rows and one narrative anchor. The API finds a medication by
 * its anchor, else by its name; between courses it takes the one starting on
 * `date`, else the one ending on `end_date` (main's `matchMedicationForImport`);
 * and it answers under the `key`. Sent as the anchor alone, both rows were given
 * the first course's dose and the second was written to the Current list with it.
 * A row with neither anchor nor name, or with no key to be answered under, is not
 * asked about. Sending `htmlname` as the API's name for the item (it takes the
 * anchor, or the name the row shows, which is the label staging gave it) was a
 * 422 on every call: it requires `html_name`,
 * so the enrichment always failed and the form was filled from the side bar alone.
 */
export function cdaMedicationImportItems(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const asked = String((item && (item.htmlname || item.name)) || '').trim();
      const key = String((item && item.key) || '');
      const date = item && item.lo ? String(item.lo).slice(0, 10) : '';
      const endDate = item && item.hi ? String(item.hi).slice(0, 10) : '';

      if (asked === '' || key === '') return null;

      return {
        key,
        html_name: asked,
        ...(date ? { date } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      };
    })
    .filter(Boolean);
}

/**
 * The API's `{fields: {<key>: {...}}}` as main's `cdaMedicationImport`
 * answered, keyed by the side bar's own key for each medication: its RxNorm and
 * NDC (from any of its codes, not only the primary), route, dose, and the
 * schedule's period split back into the value and unit the form reads.
 *
 * Read by each item's own key, which the API echoes - never by the name, which
 * two courses of one drug share.
 */
export function cdaMedicationImportFields(result, items) {
  const fields = (result && result.fields) || {};
  const out = {};

  (Array.isArray(items) ? items : []).forEach((item) => {
    const key = item && item.key ? String(item.key) : '';
    const row = key !== '' && Object.prototype.hasOwnProperty.call(fields, key) ? fields[key] : null;

    if (!row) return;

    const [period, unit] = String(row.frequency || '').trim().split(/\s+/);

    out[key] = {
      rxnorm: row.rxnorm_code || '',
      ndc: row.ndc_code || '',
      route: row.route || '',
      doseAmount: row.dose_amount || '',
      doseUnit: row.dose_unit || '',
      frequencyPeriodValue: period || '',
      frequencyPeriodUnit: unit || '',
      instructions: '',
    };
  });

  return out;
}

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

/**
 * A person the parsed header names (`CdaParser`), as one line of text: an
 * author is `{npi, name}`, the patient `{given, family, dob, gender}`.
 */
function headerParty(party) {
  if (!party) return '';
  if (typeof party !== 'object') return String(party);

  const name = party.name || [party.given, party.family].filter(Boolean).join(' ');

  return [
    name,
    party.npi ? `NPI ${party.npi}` : null,
    party.dob ? `born ${party.dob}` : null,
    party.gender || null,
  ].filter((part) => part && typeof part !== 'object').join(', ');
}

/**
 * `GET /cda-documents/{id}` (`RenderedCdaResource`) as the page main's
 * `requestCda` answered: one HTML document, a header block, then each
 * section's title and narrative (pacmny-be `CdasAction::getCda`), which
 * CdaDoc.vue writes into its iframe.
 *
 * Every header value is text and is escaped here. The narratives are an
 * outside organization's markup; the API has already passed each through its
 * allow-list sanitiser (`NarrativeSanitizer`) and they are kept as markup,
 * because the side bar finds its rows inside them by their ids.
 */
export function renderedCdaHtml(rendered) {
  if (!rendered || typeof rendered !== 'object') return '';

  const header = rendered.header || {};
  const lines = [
    ['Title', header.title],
    ['Effective', header.effective_at],
    ['Patient', headerParty(header.patient)],
    ['Custodian', headerParty(header.custodian)],
    ['Authors', (Array.isArray(header.authors) ? header.authors : []).map(headerParty).filter(Boolean).join('; ')],
    ['Document', header.document_uuid],
  ].filter(([, value]) => value);

  const headerHtml = lines
    .map(([label, value]) => `<div class='text'><span class='name'>${escapeHtml(label)}:</span> <span class='value'>${escapeHtml(value)}</span></div>`)
    .join('\n');

  const sections = (Array.isArray(rendered.sections) ? rendered.sections : [])
    .map((section) => `<hr /><div class='title'>${escapeHtml(section.title)}</div>`
      + `<div class='details'>${section.narrative_html || ''}</div>`)
    .join('\n');

  return '<html><head><style>'
    + 'table, th, td { border: 1px solid black; border-collapse: collapse; }'
    + ' paragraph { display: block; font-family: monospace; } content { font-family: monospace; }'
    + `</style></head><body><div class="header">${headerHtml}</div>${sections}</body></html>`;
}
